import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  GENEROS_DECLARATIVOS,
  GENEROS_REGISTRALES,
  NACIONALIDADES,
  OPCIONES_CONYUGE,
  PAISES_RESIDENCIA,
  PUEBLOS_ORIGINARIOS,
  REGIONES_CHILE,
  TIPOS_DIRECCION,
  comunasDeRegion,
} from '../../../../core/mocks/perfil-catalogos.mock';
import {
  BloquePerfil,
  CAMPOS_BLOQUE_PERFIL,
  DatosPersonales,
  datosPersonalesVacios,
  direccionPerfilVacia,
  SiNoPerfil,
} from '../../../../core/models/perfil.models';
import { PerfilService } from '../../../../core/services/perfil.service';
import { validarFormatoPerfil } from '../../../../core/utils/perfil-validations.util';

const BLOQUE_SIGUIENTE: Record<BloquePerfil, BloquePerfil | null> = {
  identidad: 'contacto',
  contacto: 'diversidad',
  diversidad: 'familia',
  familia: null,
};

const STORAGE_BLOQUE = 'anid.mi-perfil.bloque-abierto';

@Component({
  selector: 'app-perfil-datos-personales-tab',
  imports: [CommonModule, FormsModule],
  templateUrl: './perfil-datos-personales-tab.html',
  styleUrl: './perfil-datos-personales-tab.css',
})
export class PerfilDatosPersonalesTab implements OnInit {
  readonly tiposDireccion = TIPOS_DIRECCION;
  readonly paises = PAISES_RESIDENCIA;
  readonly nacionalidades = NACIONALIDADES;
  readonly regiones = REGIONES_CHILE;
  readonly generosRegistrales = GENEROS_REGISTRALES;
  readonly generosDeclarativos = GENEROS_DECLARATIVOS;
  readonly opcionesConyuge = OPCIONES_CONYUGE;
  readonly pueblosOriginarios = PUEBLOS_ORIGINARIOS;

  mensajeGuardado = '';
  errores: Partial<Record<string, string>> = {};
  bloqueAbierto: BloquePerfil | null = 'identidad';
  datosPersonales: DatosPersonales = datosPersonalesVacios();

  constructor(private readonly perfilService: PerfilService) {}

  ngOnInit(): void {
    this.perfilService.getDatosPersonales().subscribe((data) => {
      this.datosPersonales = {
        ...datosPersonalesVacios(),
        ...data,
        direcciones: data.direcciones?.length ? data.direcciones.map((item) => ({ ...item })) : [direccionPerfilVacia()],
      };
      this.bloqueAbierto = this.resolverBloqueInicial(data.ultimoBloque);
    });
  }

  comunas(region: string): string[] {
    return comunasDeRegion(region);
  }

  estaAbierto(bloque: BloquePerfil): boolean {
    return this.bloqueAbierto === bloque;
  }

  alternar(bloque: BloquePerfil): void {
    this.bloqueAbierto = this.bloqueAbierto === bloque ? null : bloque;
    if (this.bloqueAbierto) {
      this.persistirBloqueLocal(this.bloqueAbierto);
    }
  }

  agregarDireccion(): void {
    this.datosPersonales.direcciones = [...this.datosPersonales.direcciones, direccionPerfilVacia()];
  }

  get muestraPuebloPertenencia(): boolean {
    return this.datosPersonales.puebloOriginario === 'si';
  }

  get muestraCantidadHijos(): boolean {
    return this.datosPersonales.tieneHijos === 'si';
  }

  onPuebloOriginarioChange(valor: SiNoPerfil): void {
    if (valor !== 'si') {
      this.datosPersonales.puebloAlQuePertenece = '';
    }
  }

  onTieneHijosChange(valor: SiNoPerfil): void {
    if (valor === 'si') {
      this.datosPersonales.cantidadHijos = this.limitarCantidadHijos(this.datosPersonales.cantidadHijos);
      return;
    }
    this.datosPersonales.cantidadHijos = null;
  }

  onCantidadHijosChange(valor: number | string | null): void {
    this.datosPersonales.cantidadHijos = this.limitarCantidadHijos(valor);
  }

  private limitarCantidadHijos(valor: number | string | null): number {
    const n = Math.round(Number(valor));
    if (!Number.isFinite(n) || n < 1) return 1;
    if (n > 10) return 10;
    return n;
  }

  guardarYContinuar(bloque: BloquePerfil): void {
    this.persistirBloque(bloque, BLOQUE_SIGUIENTE[bloque] ?? bloque, false);
  }

  guardarYFinalizar(): void {
    this.persistirBloque('familia', 'familia', true);
  }

  private persistirBloque(bloque: BloquePerfil, siguiente: BloquePerfil, finalizar: boolean): void {
    this.errores = validarFormatoPerfil(this.datosPersonales);
    if (Object.keys(this.errores).length > 0) {
      this.mensajeGuardado = 'Revisa el formato de los campos marcados. Puedes guardar aunque el bloque este incompleto.';
      this.bloqueAbierto = bloque;
      return;
    }

    const payload = this.payloadDeBloque(bloque);
    payload.ultimoBloque = siguiente;

    this.perfilService.guardarDatosPersonales(payload).subscribe((data) => {
      this.datosPersonales = {
        ...this.datosPersonales,
        ...data,
        direcciones: data.direcciones?.length ? data.direcciones.map((item) => ({ ...item })) : this.datosPersonales.direcciones,
        runBloqueado: data.runPasaporte.trim().length > 0,
      };
      this.bloqueAbierto = siguiente;
      this.persistirBloqueLocal(siguiente);
      this.mensajeGuardado = finalizar
        ? 'Perfil guardado. Puedes volver despues y continuar desde este punto.'
        : 'Datos de este bloque guardados. Puedes continuar ahora o mas tarde.';
    });
  }

  private payloadDeBloque(bloque: BloquePerfil): Partial<DatosPersonales> {
    const payload: Partial<DatosPersonales> = {};
    for (const campo of CAMPOS_BLOQUE_PERFIL[bloque]) {
      (payload as Record<string, unknown>)[campo as string] = this.datosPersonales[campo];
    }
    return payload;
  }

  private resolverBloqueInicial(ultimo: BloquePerfil): BloquePerfil {
    try {
      const local = globalThis.localStorage?.getItem(STORAGE_BLOQUE) as BloquePerfil | null;
      if (local === 'identidad' || local === 'contacto' || local === 'diversidad' || local === 'familia') {
        return local;
      }
    } catch {
      /* ignore */
    }
    return ultimo || 'identidad';
  }

  private persistirBloqueLocal(bloque: BloquePerfil): void {
    try {
      globalThis.localStorage?.setItem(STORAGE_BLOQUE, bloque);
    } catch {
      /* ignore */
    }
  }
}
